import { Component, effect, EventEmitter, Input, Output, Signal, signal } from '@angular/core';
import { MapComponent } from '../map.component';
import { IFocusElement } from '../focus-element';
import { TranslateModule } from '@ngx-translate/core';
import { QPointIconModule } from '@qpoint/icons';
import { NgIf, NgFor, NgClass } from '@angular/common';

export enum FitBoundsEnum {
  Overview = 'overview',
  Location = 'location'
}

export enum ButtonType {
  ZoomButton = 'zoomButton',
  FitBoundsButton = 'fitBoundsButton',
  FocusButton = 'focusButton'
}

@Component({
    selector: 'qpoint-zoom-controls',
    templateUrl: './map-zoom-fitToBounds-controls.component.html',
    styleUrls: ['./map-zoom-fitToBounds-controls.component.scss'],
    imports: [NgIf, NgFor, NgClass, QPointIconModule, TranslateModule]
})
export class MapZoomFitToBoundsControlsComponent {
  fitBoundsEnum = FitBoundsEnum;
  buttonTypeEnum = ButtonType;

  @Input() closePopupSignal: Signal<boolean>;
  @Output() closeOtherPopups = new EventEmitter<boolean>();
  @Input() minZoom: number;
  @Input() maxZoom: number;
  @Input() mapComponent: MapComponent;

  @Input() showZoomButtons = true;
  @Input() zoomInLabel: string;
  @Input() zoomOutLabel: string;

  // Fit bounds
  @Input() showFitBoundsButton = false;
  @Input() fitBoundsLabel: string;
  @Input() overviewLabel: string;
  @Input() locationLabel: string;
  @Output() fitBoundsChanged = new EventEmitter<string>();

  public fitBoundsSelectionPopupVisible = signal<boolean>(false);

  public _activeFitBounds = signal<string>(null);
  @Input() set activeFitBounds(value: string) {
    this._activeFitBounds.set(value);
  }

  // Focus
  @Input() focusButtonToolTip: string;
  public focusSelectionPopupVisible = signal<boolean>(false);

  public _showFocusButton = signal<boolean>(false);
  @Input() set showFocusButton(value: boolean) {
    this._showFocusButton.set(value);
  }

  public _focusElements = signal<IFocusElement[]>([]);
  @Input() set focusElements(value: IFocusElement[]) {
    this._focusElements.set(value);
  }

  @Output() focusChanged = new EventEmitter<IFocusElement>();

  public _activeFocus = signal<IFocusElement>(null);
  @Input() set activeFocus(value: IFocusElement) {
    this._activeFocus.set(value);
  }

  constructor() {
    if (this.closePopupSignal) {
      effect(() => {
        if (this.closePopupSignal()) {
          this.closeSelectionPopup();
        }
      });
    }
  }

  public isActivFocusElement(focusElement: IFocusElement) {
    if (!this._activeFocus()) {
      return false
    }
    return focusElement.identifier === this._activeFocus().identifier && focusElement.id === this._activeFocus().id;
  }

  public zoomIn($event) {
    $event.preventDefault();
    this.mapComponent.map.googleMap.setZoom(this.mapComponent.map.googleMap.getZoom() + 1);
  }

  public zoomOut($event) {
    $event.preventDefault();
    this.mapComponent.map.googleMap.setZoom(this.mapComponent.map.googleMap.getZoom() - 1);
  }

  fitBounds($event) {
    $event.preventDefault();
    this.mapComponent.manualFitToBounds(false, true);
  }

  getLastButton() {
    if (this.mapComponent.showFocusButton) {
      return ButtonType.FocusButton;
    }
    if (this.mapComponent.showFitBoundsButton) {
      return ButtonType.FitBoundsButton;
    }
    if (this.mapComponent.showZoomButtons) {
      return ButtonType.ZoomButton;
    }
    return null;
  }

  getFirstButton() {
    if (this.mapComponent.isZoomAndMapTypeSamePosition()) {
      return null;
    }
    if (this.mapComponent.showZoomButtons) {
      return ButtonType.ZoomButton;
    }
    if (this.mapComponent.showFitBoundsButton) {
      return ButtonType.FitBoundsButton;
    }
    if (this.mapComponent.showFocusButton) {
      return ButtonType.FocusButton;
    }
    return null;
  }

  getLastFocusElementIdentifier() {
    return this.focusElements && this.focusElements.length > 0 ? this.focusElements[this.focusElements.length - 1].identifier : null;
  }

  toggleFitBoundsSelectionPopup($event?) {
    $event?.preventDefault();
    this.fitBoundsSelectionPopupVisible.set(!this.fitBoundsSelectionPopupVisible());
    this.closeOtherPopups.emit(true);
  }

  toggleFocusSelectionPopup($event?) {
    $event?.preventDefault();
    this.focusSelectionPopupVisible.set(!this.focusSelectionPopupVisible());
    this.closeOtherPopups.emit(true);
  }

  closeSelectionPopup() {
    this.fitBoundsSelectionPopupVisible.set(false);
    this.focusSelectionPopupVisible.set(false);
  }

  setOverviewFitBounds($event) {
    this.fitBoundsChanged.emit(FitBoundsEnum.Overview);
    this.activeFitBounds = FitBoundsEnum.Overview;
    this.toggleFitBoundsSelectionPopup($event);
  }

  setLocationFitBounds($event) {
    this.fitBoundsChanged.emit(FitBoundsEnum.Location);
    this.activeFitBounds = FitBoundsEnum.Location;
    this.toggleFitBoundsSelectionPopup($event);
  }

  setFocus($event, focusItem: IFocusElement) {
    this.focusChanged.emit(focusItem);
    this.activeFocus = focusItem;
    this.toggleFocusSelectionPopup($event);
  }
}
